import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {RED, RED_LIGHTEST} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface ErrorBadgeError {
  message: string;
  /** Optional category to group errors under a heading. */
  category?: string;
  /** Optional pre-computed count. Defaults to 1 when not provided. */
  count?: number;
}

export interface ErrorBadgeProps {
  errors: Array<ErrorBadgeError>;
  title: string;
  subtitle?: string;
}

function DisplayValueForErrorCount(props: {count: number}): JSX.Element | null {
  return props.count > 1 ? <div className={css(styles.errorCount)}>{` x${props.count}`}</div> : null;
}

/**
 * Groups errors by message, deduplicating and summing counts.
 * Respects pre-computed counts on each error (defaults to 1).
 * Returns entries in the order they were first seen.
 */
function deduplicateErrors(errors: Array<ErrorBadgeError>): Array<{message: string; count: number}> {
  const counts = new Map<string, number>();
  for (const error of errors) {
    counts.set(error.message, (counts.get(error.message) ?? 0) + (error.count ?? 1));
  }
  return Array.from(counts.entries()).map(([message, count]) => ({message, count}));
}

/** Sum the total error count across all items, respecting pre-computed counts. */
function getTotalErrorCount(errors: Array<ErrorBadgeError>): number {
  let total = 0;
  for (const error of errors) {
    total += error.count ?? 1;
  }
  return total;
}

export function ErrorBadgePanel(props: {
  errors: Array<ErrorBadgeError>;
  title: string;
  subtitle?: string;
}): JSX.Element {
  // Split errors into uncategorized and categorized groups, deduplicating each
  const {uncategorized, categorized} = useMemo(() => {
    const uncategorizedErrors: Array<ErrorBadgeError> = [];
    const categorizedMap = new Map<string, Array<ErrorBadgeError>>();
    for (const error of props.errors) {
      if (error.category == null) {
        uncategorizedErrors.push(error);
      } else {
        const list = categorizedMap.get(error.category) ?? [];
        list.push(error);
        categorizedMap.set(error.category, list);
      }
    }
    return {
      uncategorized: deduplicateErrors(uncategorizedErrors),
      categorized: Array.from(categorizedMap.entries()).map(([category, errors]) => ({
        category,
        errors: deduplicateErrors(errors),
      })),
    };
  }, [props.errors]);

  return (
    <div className={css(styles.errorListWrapper)}>
      <div>
        <div className={css(styles.errorPanelHeading)}>{props.title}</div>
        {props.subtitle}
      </div>
      {uncategorized.map(({message, count}, index) => (
        <div className={classNames(css(styles.error))} key={index}>
          <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
          {message}
          <DisplayValueForErrorCount count={count} />
        </div>
      ))}
      {categorized.map(({category, errors}) => (
        <div key={category}>
          <div className={classNames(css(styles.errorPanelHeading))}>{category}</div>
          {errors.map(({message, count}, index) => (
            <div className={css(styles.error)} key={index}>
              <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
              {message}
              <DisplayValueForErrorCount count={count} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Reusable error badge component that displays a count of errors
 * and shows a popover with error details on click.
 *
 * Errors are displayed in a panel with optional category grouping.
 * Duplicate error messages within the same category are deduplicated with a count.
 */
export default function ErrorBadge(props: ErrorBadgeProps): JSX.Element {
  const totalErrorCount: number = useMemo(() => getTotalErrorCount(props.errors), [props.errors]);
  return (
    <Popover
      placement={FloatingPlacement.BOTTOM}
      renderContent={() => (
        <ErrorBadgePanel errors={props.errors} title={props.title} subtitle={props.subtitle} />
      )}
    >
      <div
        className={css(styles.errorStatusBadge)}
        {...getTestProps({component: 'ErrorBadge', element: 'badge'})}
      >
        {totalErrorCount}
        <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
      </div>
    </Popover>
  );
}

const styles = StyleSheet.create({
  errorListWrapper: {
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  errorCount: {
    fontWeight: 600,
  },
  error: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginBottom: '4px',
  },
  errorPanelHeading: {
    fontWeight: 600,
    fontSize: '13px',
  },
  errorStatusBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 10px',
    margin: '4px',
    gap: '8px',
    height: '32px',
    fontSize: '13px',
    fontWeight: 600,
    background: RED_LIGHTEST,
    borderRadius: '4px',
  },
  validationErrorIcon: {
    color: RED,
    width: '16px',
    height: '16px',
  },
});
